import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import api from "../../services/api";
import EditModal from "../ui/EditModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPencil } from "@fortawesome/free-solid-svg-icons";

const INITIAL_FORM_STATE = {
  idUsuario: null,
  username: "",
  nombre: "",
  apellido: "",
  email: "",
  idPerfil: "",
  password: "",
};

export default function AdministracionUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [perfiles, setPerfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const loadUsuarios = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/usuarios");
      setUsuarios(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error al cargar usuarios:", err);
      setError("No se pudo cargar la lista de usuarios.");
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPerfiles = async () => {
    try {
      const { data } = await api.get("/perfiles");
      setPerfiles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error al cargar perfiles:", err);
      setPerfiles([]);
    }
  };

  useEffect(() => {
    loadUsuarios();
    loadPerfiles();
  }, []);

  const resetForm = () => {
    setForm(INITIAL_FORM_STATE);
    setSelectedUserId(null);
    setMessage(null);
    setError(null);
    setIsModalOpen(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUserId(null);
    setForm(INITIAL_FORM_STATE);
    setError(null);
  };

  const handleEdit = async (user) => {
    setError(null);
    setMessage(null);
    setSelectedUserId(user.idUsuario);
    setForm({
      idUsuario: user.idUsuario,
      username: user.username || "",
      nombre: user.nombre || "",
      apellido: user.apellido || "",
      email: user.email || "",
      idPerfil: user.idPerfilLegacy || "",
      password: "",
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (user) => {
    const confirmed = window.confirm(`¿Eliminar al usuario ${user.username || user.nombre || user.idUsuario}?`);
    if (!confirmed) {
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const { data } = await api.delete(`/usuarios/${user.idUsuario}`);
      setMessage(data?.msg || "Usuario eliminado correctamente.");
      resetForm();
      await loadUsuarios();
    } catch (err) {
      console.error("Error al eliminar usuario:", err);
      setError(err?.response?.data?.msg || "No se pudo eliminar el usuario.");
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (values) => {
    if (!values.idUsuario) {
      setError("Selecciona un usuario para editar.");
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const payload = {
        Username: values.username,
        Nombre: values.nombre,
        Apellido: values.apellido,
        Email: values.email,
        IdPerfil: Number(values.idPerfil) || null,
      };

      if (values.password) {
        payload.Password = values.password;
      }

      const { data } = await api.put(`/usuarios/${values.idUsuario}`, payload);
      setMessage(data?.msg || "Usuario actualizado correctamente.");
      setIsModalOpen(false);
      resetForm();
      await loadUsuarios();
    } catch (err) {
      console.error("Error al actualizar usuario:", err);
      setError(err?.response?.data?.msg || "No se pudo actualizar el usuario.");
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        name: "ID",
        selector: (row) => row.idUsuario,
        sortable: true,
        width: "90px",
      },
      {
        name: "Usuario",
        selector: (row) => row.username || "-",
        sortable: true,
      },
      {
        name: "Nombre",
        selector: (row) => row.nombre || "-",
      },
      {
        name: "Apellido",
        selector: (row) => row.apellido || "-",
      },
      {
        name: "Email",
        selector: (row) => row.email || "-",
      },
      {
        name: "Perfil",
        selector: (row) => {
          const perfil = perfiles.find((p) => Number(p.idPerfil) === Number(row.idPerfilLegacy));
          return perfil?.nombre || row.idPerfilLegacy || "-";
        },
      },
      {
        name: "Acciones",
        cell: (row) => (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="button is-small is-info mr-2 icon-pencil"
              title="Editar"
              onClick={() => handleEdit(row)}
            >
              <FontAwesomeIcon icon={faPencil} />
            </button>
            <button
              type="button"
              className="button is-small is-danger"
              title="Eliminar"
              onClick={() => handleDelete(row)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
      },
    ],
    [perfiles]
  );

  return (
    <div style={{ padding: "32px 24px", maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 28, marginBottom: 8 }}>Administración de Usuarios</h1>
          <p style={{ margin: 0, color: "#4a4a4a" }}>
            Visualiza, edita o elimina los usuarios registrados en el sistema.
          </p>
        </div>
        <button type="button" className="button is-primary" onClick={loadUsuarios}>
          Actualizar lista
        </button>
      </div>

      {message && (
        <div className="notification is-success" style={{ marginBottom: 16 }}>
          {message}
        </div>
      )}
      {error && (
        <div className="notification is-danger" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ background: "#fff", borderRadius: 12, padding: 16, boxShadow: "0 6px 20px rgba(0,0,0,0.05)" }}>
        <h2 style={{ fontSize: 20, marginBottom: 12 }}>Usuarios</h2>
        <DataTable
          columns={columns}
          data={usuarios}
          progressPending={loading}
          noDataComponent="No se encontraron usuarios"
          pagination
          dense
        />
      </div>

      {isModalOpen && (
        <EditModal
          title={`Editar Usuario ${form.username || ""}`}
          initialValues={form}
          fields={[
            { name: "username", label: "Usuario", type: "text", required: true },
            { name: "nombre", label: "Nombre", type: "text", required: false },
            { name: "apellido", label: "Apellido", type: "text", required: false },
            { name: "email", label: "Email", type: "email", required: true },
            { name: "idPerfil", label: "Perfil", type: "select", options: perfiles.map((perfil) => ({ value: perfil.idPerfil, label: perfil.nombre })) },
            { name: "password", label: "Contraseña (opcional)", type: "password", required: false },
          ]}
          onSubmit={handleSubmit}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
